<template lang="pug">
.form-group(:class="formGroupClass")
  label(v-if="label" :for="inputId" class="form-label") 
    | {{ label }}
    span.text-danger(v-if="required")  *
  .input-group
    span.input-group-text(v-if="withIcon")
      i.fa-solid.fa-calendar
    input.form-control(
      :id="inputId"
      :name="name"
      type="text"
      :value="formattedValue"
      @input="handleInput"
      @blur="handleBlur"
      :placeholder="placeholder"
      :disabled="disabled"
      :class="inputClass"
      autocomplete="off"
      maxlength="10"
    )
  small.form-text.text-muted(v-if="helpText") {{ helpText }}
  .invalid-feedback(v-if="error" :class="errorClass") {{ errorMessage }}
  .valid-feedback(v-if="isValid && showValidFeedback" :class="validClass") 
    | {{ $I18n.t('vue.date_input.valid_date') }}
</template>

<script>
export default {
  name: 'DateInput',
  props: {
    value: {
      type: String,
      default: ''
    },
    label: {
      type: String,
      default: ''
    },
    name: {
      type: String,
      required: true
    },
    placeholder: {
      type: String,
      default: 'dd/mm/yyyy'
    },
    required: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    withIcon: {
      type: Boolean,
      default: true
    },
    helpText: {
      type: String,
      default: ''
    },
    formGroupClass: {
      type: String,
      default: ''
    },
    showValidFeedback: {
      type: Boolean,
      default: false
    },
    minDate: {
      type: String,
      default: null
    },
    maxDate: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      inputValue: this.value,
      error: false,
      errorMessage: '',
      touched: false
    };
  },
  computed: {
    inputId() {
      return `date-input-${this.name}`;
    },
    formattedValue() {
      return this.inputValue;
    },
    inputClass() {
      return {
        'is-invalid': this.error && this.touched,
        'is-valid': this.isValid && this.touched && this.showValidFeedback
      };
    },
    errorClass() {
      return { 'd-block': this.error };
    },
    validClass() {
      return { 'd-block': this.isValid };
    },
    isValid() {
      return !this.error && this.inputValue && this.validateDateFormat(this.inputValue);
    }
  },
  watch: {
    value(newVal) {
      this.inputValue = newVal;
    }
  },
  methods: {
    handleInput(event) {
      let value = event.target.value;
      
      // Eliminar caracteres no numéricos excepto /
      value = value.replace(/[^\d/]/g, '');
      
      // Aplicar máscara automáticamente
      if (value.length === 2 && !value.includes('/')) {
        value += '/';
      } else if (value.length === 5 && value.split('/').length === 2) {
        value += '/';
      }
      
      // Limitar longitud
      if (value.length > 10) {
        value = value.substring(0, 10);
      }
      
      this.inputValue = value;
      this.$emit('input', value);
      
      // Validar en tiempo real si tiene formato completo
      if (value.length === 10) {
        this.validate();
      } else {
        this.error = false;
      }
    },
    
    handleBlur() {
      this.touched = true;
      this.validate();
    },
    
    validate() {
      if (this.required && !this.inputValue) {
        this.error = true;
        this.errorMessage = this.$I18n.t('vue.date_input.required');
        return false;
      }
      
      if (this.inputValue && !this.validateDateFormat(this.inputValue)) {
        this.error = true;
        this.errorMessage = this.$I18n.t('vue.date_input.invalid_format');
        return false;
      }
      
      if (this.inputValue && this.validateDateFormat(this.inputValue)) {
        const date = this.parseDate(this.inputValue);
        
        // Validar fecha mínima
        if (this.minDate) {
          const minDate = this.parseDate(this.minDate);
          if (date < minDate) {
            this.error = true;
            this.errorMessage = this.$I18n.t('vue.date_input.min_date', { date: this.minDate });
            return false;
          }
        }
        
        // Validar fecha máxima
        if (this.maxDate) {
          const maxDate = this.parseDate(this.maxDate);
          if (date > maxDate) {
            this.error = true;
            this.errorMessage = this.$I18n.t('vue.date_input.max_date', { date: this.maxDate });
            return false;
          }
        }
      }
      
      this.error = false;
      this.errorMessage = '';
      return true;
    },
    
    validateDateFormat(dateString) {
      // Validar formato dd/mm/yyyy
      const regex = /^(\d{2})\/(\d{2})\/(\d{4})$/;
      const match = dateString.match(regex);
      
      if (!match) return false;
      
      const day = parseInt(match[1], 10);
      const month = parseInt(match[2], 10);
      const year = parseInt(match[3], 10);
      
      // Validar rangos básicos
      if (month < 1 || month > 12) return false;
      if (day < 1 || day > 31) return false;
      if (year < 1900 || year > 2100) return false;
      
      // Validar días por mes
      const daysInMonth = new Date(year, month, 0).getDate();
      if (day > daysInMonth) return false;
      
      return true;
    },
    
    parseDate(dateString) {
      const [day, month, year] = dateString.split('/').map(Number);
      return new Date(year, month - 1, day);
    },
    
    reset() {
      this.inputValue = '';
      this.error = false;
      this.errorMessage = '';
      this.touched = false;
      this.$emit('input', '');
    }
  }
};
</script>

<style scoped>
.input-group-text {
  background-color: #f8f9fa;
  border-right: 0;
}

.form-control:focus + .input-group-text {
  border-color: #80bdff;
}

.form-control.is-invalid {
  border-color: #dc3545;
}

.form-control.is-valid {
  border-color: #28a745;
}
</style>
